import React, { useRef, useState } from "react";
import { IBowser } from "bowser";
import { Image, ModelFiles, Video } from "../model";
import { useIsAugmentedRealitySupported, SeeIn3DButton } from "~/common/MediaViewerV2/FullViewDisplay/SeeIn3DButton/SeeIn3DButton";
import ProductImageDisclaimer from "~/common/MediaViewerV2/FullViewDisplay/ProductImageDisclaimer/ProductImageDisclaimer";
import ImageMediaRenderer from "~/common/MediaViewerV2/FullViewDisplay/ImageRenderers/ImageMediaRenderer";
import ImageGalleryRenderer from "~/common/MediaViewerV2/FullViewDisplay/ImageRenderers/ImageGalleryRenderer";
import ModelGalleryRenderer from "~/common/MediaViewerV2/FullViewDisplay/ModelRenderers/ModelGalleryRenderer";
import ModelMediaRenderer from "~/common/MediaViewerV2/FullViewDisplay/ModelRenderers/ModelMediaRenderer";
import VideoGalleryRenderer from "~/common/MediaViewerV2/FullViewDisplay/VideoRenderers/VideoGalleryRenderer";
import VideoMediaRenderer from "~/common/MediaViewerV2/FullViewDisplay/VideoRenderers/VideoMediaRenderer";
import { isWebGlSupported } from "../utils";
import useIsMounted from "~/common/useIsMounted";
export interface FullViewDisplayProps {
  media: (ModelFiles | Image | Video)[];
  activeItemNumberIndex: number;
  slides: React.MutableRefObject<{
    [p: string]: HTMLDivElement;
  }>;
  selectedMediaIndex: number;
  setSelectedMediaIndex: React.Dispatch<React.SetStateAction<number>>;
  thumbnails: React.MutableRefObject<HTMLDivElement[]>;
  title: string;
  openZoom: () => void;
  overlayOpen: boolean;
  isMobileScreenSize: boolean;
  placeholderImageUrl: string;
  bowser?: Readonly<IBowser>;
  productImageDisclaimer?: string;
  modelFiles?: ModelFiles;
  isImageSelected: boolean;
  productDimensionsIsEnabled?: boolean;
}
const FullViewDisplay = ({
  media,
  selectedMediaIndex,
  activeItemNumberIndex,
  slides,
  setSelectedMediaIndex,
  thumbnails,
  title,
  openZoom,
  overlayOpen,
  isMobileScreenSize,
  placeholderImageUrl,
  bowser,
  productImageDisclaimer,
  modelFiles,
  isImageSelected,
  productDimensionsIsEnabled
}: FullViewDisplayProps) => {
  const pageLoad = useRef(true);
  const isMounted = useIsMounted();
  const cachedActiveItemNumberIndex = useRef(activeItemNumberIndex);
  const [showAugmentedRealityButton, setShowAugmentedRealityButton] = useState(false);
  useIsAugmentedRealitySupported(modelFiles, setShowAugmentedRealityButton);
  const baseBorderStyling = "before:border before:border-gray-600";
  const selectedTileButtonBorderStyling = "before:border-2 before:border-blue-400";
  const galleryRef = useRef<HTMLDivElement>(null);
  const galleryHasXOverflow = () => {
    if (!galleryRef.current) {
      return false;
    }
    if (galleryRef.current.scrollWidth > galleryRef.current.clientWidth) {
      return true;
    }
    return false;
  };
  const mobileStyling = "flex flex-row flex-nowrap overflow-hidden overflow-x-auto";
  const desktopStyling = "xl:overflow-y-auto xl:absolute xl:flex-wrap xl:right-0 xl:inset-t-0 xl:w-52 xl:max-h-[95%] xl:mt-3 xl:mr-4 xl:top-0 xl:justify-start";
  const addSlide = (slide: HTMLDivElement, index: number) => {
    if (activeItemNumberIndex !== cachedActiveItemNumberIndex.current) {
      // eslint-disable-next-line no-param-reassign
      slides.current = {};
      cachedActiveItemNumberIndex.current = activeItemNumberIndex;
    }
    if (pageLoad.current) {
      pageLoad.current = false;
      return;
    }

    // eslint-disable-next-line no-param-reassign
    slides.current[index] = slide;
  };
  const addThumbnail = (thumbnail: HTMLDivElement) => thumbnails.current.push(thumbnail);
  const handleThumbnailClick = (e: React.MouseEvent<HTMLAnchorElement>, index: number) => {
    e.preventDefault();
    slides.current[index]?.scrollIntoView({
      block: "nearest"
    });
    setSelectedMediaIndex(index);
  };
  const isImage = (medium: ModelFiles | Image | Video): medium is Image => {
    return "extraLarge" in medium;
  };
  const mediaRenderer = (medium: ModelFiles | Image | Video, index: number) => {
    if ("isPrimaryDetailImage" in medium) {
      return <ImageMediaRenderer index={index} image={medium} title={title} openZoom={openZoom} overlayOpen={overlayOpen} isMobileScreenSize={isMobileScreenSize} placeholderImageUrl={placeholderImageUrl} bowser={bowser} />;
    }
    if ("relativeGlbFilePath" in medium) {
      const image = media.filter(m => "isPrimaryDetailImage" in m)[1];
      const poster = image && isImage(image) ? image.extraLarge : undefined;
      return <ModelMediaRenderer model={medium} poster={poster} productDimensionsIsEnabled={productDimensionsIsEnabled ?? false} />;
    }
    return <VideoMediaRenderer index={index} selectedMediaIndex={selectedMediaIndex} video={medium} data-sentry-element="VideoMediaRenderer" data-sentry-component="mediaRenderer" data-sentry-source-file="FullViewDisplay.tsx" />;
  };
  const galleryRenderer = (medium: ModelFiles | Image | Video, index: number) => {
    if ("isPrimaryDetailImage" in medium) {
      return <ImageGalleryRenderer index={index} media={media} handleThumbnailClick={handleThumbnailClick} borderStyling={selectedMediaIndex === index ? selectedTileButtonBorderStyling : baseBorderStyling} image={medium} />;
    }
    if ("relativeGlbFilePath" in medium) {
      return <ModelGalleryRenderer index={index} media={media} handleThumbnailClick={handleThumbnailClick} borderStyling={selectedMediaIndex === index ? selectedTileButtonBorderStyling : baseBorderStyling} productDimensionsIsEnabled={productDimensionsIsEnabled ?? false} />;
    }
    return <VideoGalleryRenderer index={index} media={media} video={medium} handleThumbnailClick={handleThumbnailClick} borderStyling={selectedMediaIndex === index ? selectedTileButtonBorderStyling : baseBorderStyling} data-sentry-element="VideoGalleryRenderer" data-sentry-component="galleryRenderer" data-sentry-source-file="FullViewDisplay.tsx" />;
  };
  return <div className="
        bg-white
        border-gray-200
        xs:rounded
        xs:rounded-b-none
        border-solid
        border
        border-l-0
        xs:border-l
        border-r-0
        xs:border-r
        border-b-0
        flex
        flex-wrap
        overflow-hidden
        p-5
        relative
        md:rounded-b
        md:border-b
        md:mb-5
        lt:rounded-b-none
        lt:border-b-0
        lt:mb-0
        xl:flex-nowrap
      " data-sentry-component="FullViewDisplay" data-sentry-source-file="FullViewDisplay.tsx">
      <div className={`
          overflow-hidden
          w-full
          ${media.length > 1 ? "xl:pr-52" : ""}
        `}>
        <div className="overflow-hidden w-full">
          <div className="
              scrollbar-width-none
              flex
              items-start
              overflow-x-auto
              overflow-y-hidden
              scroll-snap-x
              scroll-snap-x-mandatory
            ">
            {media.map((medium, index) => {
            return <div id={`slide-${index}`}
            // eslint-disable-next-line react/no-array-index-key
            key={`media${index}`} className="
                    items-center
                    justify-center
                    flex-shrink-0
                    mr-32
                    relative
                    w-full
                    origin-center
                    scale-100
                    scroll-snap-align-start
                    flex
                    " data-index={index} data-testid="main-slider-item" ref={(slide: HTMLDivElement) => addSlide(slide, index)}>
                  {mediaRenderer(medium, index)}
                </div>;
          })}
          </div>
        </div>

        {media?.length > 1 && <div ref={galleryRef} className={`${mobileStyling} ${desktopStyling} ${galleryHasXOverflow() ? "justify-start" : "justify-center"}`}>
            {media.map((medium, index) => {
          return <div className={`flex-shrink-0 m-1 lg:m-2 w-12 h-12 xs:w-16 xs:h-16 lg:w-20 lg:h-20 bg-white ${selectedMediaIndex === index && " active"} ${
          // Hide the regular 3d button if it's disallowed because we're showing the AR "See it in your space" button
          isMounted && (!isWebGlSupported || showAugmentedRealityButton) && "relativeGlbFilePath" in medium ? "hidden" : ""}
                  `}
          // eslint-disable-next-line react/no-array-index-key
          key={`pic-${index}`} ref={addThumbnail}>
                  {galleryRenderer(medium, index)}
                </div>;
        })}
          </div>}

        <ProductImageDisclaimer isImageSelected={isImageSelected} productImageDisclaimer={productImageDisclaimer} data-sentry-element="ProductImageDisclaimer" data-sentry-source-file="FullViewDisplay.tsx" />

        {showAugmentedRealityButton && <SeeIn3DButton modelFiles={modelFiles} />}
      </div>
    </div>;
};
export default FullViewDisplay;